// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@bg/App/Store/Reducers";
import Button, { ButtonPresentationProps } from "@bg/MobiusOverrides/Button/Button";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import {
    getCurrentPage,
    getLocation,
    removeAbsoluteUrl,
} from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HeaderContext } from "@insite/content-library/Pages/Header";
import SignInChangePasswordForm from "@insite/content-library/Widgets/SignIn/SignInChangePasswordForm";
import SignInResetPasswordForm from "@insite/content-library/Widgets/SignIn/SignInResetPasswordForm";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    changePasswordInstructions = "changePasswordInstructions",
    fullWidthButton = "fullWidthButton",
    selectParent = "selectParent",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.changePasswordInstructions]: string;
        [fields.fullWidthButton]: boolean;
        [fields.selectParent]: string;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ returnUrl?: string; externalError?: string }>(location.search);
    const pageType = getCurrentPage(state).type;
    const isSignInPage = pageType === "SignInPage";
    const isConfirmationPage = pageType === "OrderConfirmationPage" || pageType === "OnePageCheckoutConfirmationPage";
    const returnUrl = isSignInPage
        ? removeAbsoluteUrl(parsedQuery.returnUrl)
        : `${location.pathname}${location.search}`;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");

    return {
        accountSettings: getSettingsCollection(state).accountSettings,
        returnUrl,
        externalError: parsedQuery.externalError,
        isConfirmationPage,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${location.search}` : undefined,
        loginErr: state?.commonActions?.loginErr || "",
        isForgetPassModalOpen: state?.commonActions?.isForgetPassModalOpen || false,
    };
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

const mapDispatchToProps = {
    signIn,
};

export interface SignInExistingAccountStyles {
    form?: InjectableCss;
    signInWrapper?: InjectableCss;
    rememberMeCheckbox?: CheckboxProps;
    rememberMeCheckboxGroup?: CheckboxGroupComponentProps;
    userNameTextField?: TextFieldProps;
    signInGridContainer?: GridContainerProps;
    icon?: IconProps;
    passwordTextField?: TextFieldProps;
    signInButton?: ButtonPresentationProps;
    signInButtonGridItem?: GridItemProps;
    forgotPasswordGridItem?: GridItemProps;
    registerGridItem?: GridItemProps;
    rememberMeGridItem?: GridItemProps;
    passwordGridItem?: GridItemProps;
    userNameGridItem?: GridItemProps;
    signInExistingAccountTitle?: TypographyProps;
    forgotPasswordLink?: LinkPresentationProps;
    resetPasswordModal?: ModalPresentationProps;
    changePasswordModal?: ModalPresentationProps;
    registerSection?: InjectableCss;
}

export const signInExistingAccountStyles: SignInExistingAccountStyles = {
    signInWrapper: {
        css: css`
            max-width: 346px;
            color: ${getColor("common.ui5")};
            padding: 29px;

            input {
                padding-left: 17px !important;
            }
        `,
    },
    icon: {
        css: css`
            padding-top: 50px;
        `,
    },
    signInExistingAccountTitle: {
        variant: "h5",
    },
    userNameGridItem: {
        width: 12,
        css: css`
            padding-top: 10px;
            padding-bottom: 10px;
            label {
                font-weight: ${fontConfig.weight.semiBold};
            }
        `,
    },
    passwordGridItem: {
        width: 12,
        css: css`
            padding-top: 10px;
            padding-bottom: 10px;
            label {
                font-weight: ${fontConfig.weight.semiBold};
            }
        `,
    },
    signInButtonGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
            padding-top: 0;
        `,
    },
    rememberMeGridItem: {
        width: 6,
        css: css`
            padding-bottom: 10px;
            padding-top: 10px;
            padding-right: 0;
        `,
    },
    forgotPasswordGridItem: {
        width: 6,
        css: css`
            justify-content: flex-end;
            display: flex;
            align-items: center;
            padding-bottom: 10px;
            padding-top: 10px;
            padding-left: 0;
        `,
    },
    registerGridItem: {
        width: 12,
        css: css`
            padding-top: 5px;
        `,
    },
    registerSection: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: center;
            a {
                margin-left: 4px;
            }
        `,
    },
    forgotPasswordLink: {},
    signInButton: {
        css: css`
            width: 100%;
        `,
    },
    resetPasswordModal: {
        sizeVariant: "medium",
    },
    changePasswordModal: {
        sizeVariant: "small",
    },
};

/**
 * @deprecated Use signInExistingAccountStyles instead.
 */
export const signInExistingAccount = signInExistingAccountStyles;
const styles = signInExistingAccountStyles;
const StyledForm = getStyledWrapper("form");

const SignInExistingAccount = ({
    signIn,
    fields,
    accountSettings,
    returnUrl,
    externalError,
    isConfirmationPage,
    myAccountPageLink,
    theme,
    createAccountUrl,
    history,
    loginErr,
    isForgetPassModalOpen,
}: Props) => {
    const [rememberMe, setRememberMe] = useState(false);
    const [keepMeSignedIn, setKeepMeSignedIn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [isSignInClicked, setIsSignInClicked] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(isForgetPassModalOpen);
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState(externalError || loginErr || "");
    const [redirectUrl, setRedirectUrl] = useState("");
    const dispatch = useAppDispatch();

    const showRememberMe = accountSettings.rememberMe;
    const showKeepMeSignedIn = accountSettings.keepMeSignedIn;

    useEffect(() => {
        setRedirectUrl(window.location.href.replace(window.location.origin, ""));
    }, [redirectUrl]);

    const signInHandler = (newPassword?: string) => {
        const pass = newPassword || password;
        setIsSignInClicked(true);
        if (!userName || !pass) {
            return;
        }

        if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
            return;
        }

        setErrorMessage("");

        signIn({
            userName,
            password: pass,
            rememberMe,
            keepMeSignedIn,
            returnUrl: isConfirmationPage ? myAccountPageLink?.url : returnUrl,
            skipRedirect: !isConfirmationPage,
            onError: (error, statusCode) => {
                if (statusCode === 422) {
                    setIsChangePasswordModalOpen(true);
                    return;
                }
                setErrorMessage(error);
                dispatch({
                    type: "commonActionSetter/SetAction",
                    payload: {
                        name: "loginErr",
                        value: error,
                    },
                });
            },
        });
    };

    const submitHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        signInHandler();
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    const onResetPasswordClose = () => {
        setIsResetPasswordModalOpen(false);
    };

    const onResetPasswordOpen = () => {
        setIsResetPasswordModalOpen(true);
    };

    const onChangePasswordClose = () => {
        setIsChangePasswordModalOpen(false);
    };

    const onPasswordChanged = (newPassword: string) => {
        setPassword(newPassword);
        setIsChangePasswordModalOpen(false);
        signInHandler(newPassword);
    };

    const validateUserName = () => {
        if (isSignInClicked) {
            if (userName) {
                if (accountSettings.useEmailAsUserName && !validateEmail(userName)) {
                    return siteMessage("SignInInfo_Email_Validation");
                }
            } else {
                return accountSettings.useEmailAsUserName
                    ? siteMessage("SignInInfo_Email_Required")
                    : siteMessage("SignInInfo_UserName_Required");
            }
        }

        return "";
    };

    const userErrorMessage = validateUserName();
    const passwordErrorMessage =
        !password && isSignInClicked ? siteMessage("SignInInfo_Password_Required") : errorMessage || "";
    const isSlider = fields.selectParent === "slider";
    const signInButtonCss = css`
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    width: 100%;
                `,
                css`
                    width: 100%;
                `,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
                fields.fullWidthButton
                    ? css`
                          width: 100%;
                      `
                    : null,
            ])}
    `;

    return (
        <>
            <StyledWrapper {...styles.signInWrapper} className="sign-in-wrapper">
                {isSlider && (
                    <Typography {...styles.signInExistingAccountTitle}>{translate("Please Sign In")}</Typography>
                )}
                <StyledForm {...styles.form} onSubmit={submitHandler} noValidate>
                    <GridContainer {...styles.signInGridContainer}>
                        <GridItem {...styles.userNameGridItem}>
                            <TextField
                                id="userName"
                                {...styles.userNameTextField}
                                label={
                                    !isSlider
                                        ? accountSettings.useEmailAsUserName
                                            ? translate("Email")
                                            : translate("Username")
                                        : " "
                                }
                                onChange={e => {
                                    setErrorMessage("");
                                    setUserName(e.currentTarget.value);
                                }}
                                value={userName}
                                error={userErrorMessage}
                                placeholder={
                                    accountSettings.useEmailAsUserName ? translate("Email") : translate("Username")
                                }
                                autoComplete="username"
                                data-test-selector="signIn_userName"
                            />
                        </GridItem>
                        <GridItem {...styles.passwordGridItem}>
                            <TextField
                                id="password"
                                {...styles.passwordTextField}
                                label={!isSlider ? translate("Password") : " "}
                                placeholder={translate("Password")}
                                onChange={e => {
                                    setErrorMessage("");
                                    setPassword(e.currentTarget.value);
                                }}
                                value={password}
                                error={passwordErrorMessage}
                                iconProps={{ ...styles.icon, src: showPassword ? EyeOff : Eye }}
                                iconClickableProps={{
                                    onClick: () => {
                                        setShowPassword(!showPassword);
                                    },
                                    type: "button",
                                }}
                                clickableText={password}
                                autoComplete="current-password"
                                data-test-selector="signIn_password"
                                type={showPassword ? "text" : "password"}
                            />
                        </GridItem>
                        <GridItem {...styles.rememberMeGridItem}>
                            {showRememberMe && (
                                <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                    <Checkbox
                                        uid="rememberMe"
                                        {...styles.rememberMeCheckbox}
                                        checked={rememberMe}
                                        onChange={(_, value) => setRememberMe(value)}
                                    >
                                        {translate("Remember Me")}
                                    </Checkbox>
                                </CheckboxGroup>
                            )}
                            {showKeepMeSignedIn && (
                                <CheckboxGroup {...styles.rememberMeCheckboxGroup}>
                                    <Checkbox
                                        uid="keepMeSignedIn"
                                        {...styles.rememberMeCheckbox}
                                        checked={keepMeSignedIn}
                                        onChange={(_, value) => setKeepMeSignedIn(value)}
                                    >
                                        {translate("Keep Me Signed In")}
                                    </Checkbox>
                                </CheckboxGroup>
                            )}
                        </GridItem>
                        <GridItem {...styles.forgotPasswordGridItem}>
                            <Link
                                {...styles.forgotPasswordLink}
                                type="button"
                                onClick={onResetPasswordOpen}
                                data-test-selector="signIn_forgotPassword"
                            >
                                {translate("Forgot Password?")}
                            </Link>
                            <Modal
                                headline={translate("Reset Password")}
                                {...styles.resetPasswordModal}
                                isOpen={isResetPasswordModalOpen}
                                handleClose={onResetPasswordClose}
                                data-test-selector="resetPasswordModal"
                            >
                                <SignInResetPasswordForm onClose={onResetPasswordClose} />
                            </Modal>
                        </GridItem>
                        <GridItem {...styles.signInButtonGridItem}>
                            <Button {...styles.signInButton} type="submit" data-test-selector="signIn_submit">
                                {translate("Sign In")}
                            </Button>
                            <Modal
                                headline={translate("Change Password")}
                                {...styles.changePasswordModal}
                                isOpen={isChangePasswordModalOpen}
                                handleClose={onChangePasswordClose}
                            >
                                <SignInChangePasswordForm
                                    enteredUserName={userName}
                                    enteredPassword={password}
                                    instructionsText={fields.changePasswordInstructions}
                                    onPasswordChanged={onPasswordChanged}
                                />
                            </Modal>
                        </GridItem>
                        {isSlider && (
                            <GridItem {...styles.registerGridItem}>
                                <StyledWrapper {...styles.registerSection}>
                                    <Typography as="span">{translate("Don’t have an account?")}</Typography>
                                    <Link {...styles.forgotPasswordLink} href={createAccountUrl}>
                                        {translate("Register Now")}
                                    </Link>
                                </StyledWrapper>
                            </GridItem>
                        )}
                    </GridContainer>
                </StyledForm>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(withHistory(SignInExistingAccount))),
    definition: {
        allowedContexts: ["SignInPage", "SignInOverlay", HeaderContext as any, "Header"],
        group: "Sign In",
        icon: "LogIn",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.changePasswordInstructions,
                editorTemplate: "RichTextField",
                displayName: "Change Password Instructions",
                defaultValue: "A password change is required for your account before you may continue.",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                    code: {},
                },
                sortOrder: 0,
            },
            {
                name: fields.fullWidthButton,
                displayName: "Full Width Button",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.selectParent,
                displayName: "Parent:",
                editorTemplate: "RadioButtonsField",
                isEnabled: item => true,
                options: [
                    {
                        displayName: "Drawer",
                        value: "drawer",
                        tooltip: "this means that the parent is drawer",
                    },
                    {
                        displayName: "Slider",
                        value: "slider",
                        tooltip: "this means that the parent is drawer",
                    },
                ],
                defaultValue: "slider",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
